import { useEffect, useRef, useState, useCallback } from 'react'
import { customerApi as api, authApi } from '../../services/api'

// Customer-side handover routes are at /api/customer/handover/* — NOT
// under /api/v1 — so we use customerApi (baseURL /api) rather than the
// default `api` instance (baseURL /api/v1). SSE URL derived from authApi.
const API_PREFIX = (authApi.defaults?.baseURL || '/api').replace(/\/+$/, '')

/**
 * HandoverSettings — the owner's view inside Customer Dashboard.
 *
 *   LEFT:   Agents panel  (create / activate / rename / delete sub-users)
 *                         + master toggle for the feature
 *   RIGHT:  Live monitor  (all handovers for this customer, read-only).
 *                         The owner CANNOT send messages — doing so is
 *                         gated by the backend to the assigned sub-user
 *                         agent. If the owner wants to join a chat, they
 *                         create an agent and sign in as that agent.
 */
export default function HandoverSettings() {
  const [agents, setAgents] = useState([])
  const [stats, setStats] = useState({ total_agents: 0, active_agents: 0, inactive_agents: 0, handover_enabled: false })
  const [enabled, setEnabled] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState(null)
  const [live, setLive] = useState([])
  const [openId, setOpenId] = useState(null)
  const [openDetail, setOpenDetail] = useState(null)
  const [error, setError] = useState('')
  const esRef = useRef(null)
  const openIdRef = useRef(null)

  const load = useCallback(async () => {
    try {
      const [a, s, l] = await Promise.all([
        api.get('/customer/handover/sub-users'),
        api.get('/customer/handover/sub-users/stats'),
        api.get('/customer/handover/live'),
      ])
      setAgents(a.data)
      setStats(s.data)
      setEnabled(s.data.handover_enabled)
      setLive(l.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load handover data.')
    }
  }, [])

  const loadDetail = useCallback(async (handoverId) => {
    try {
      const r = await api.get(`/customer/handover/live/${handoverId}`)
      setOpenDetail(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load conversation.')
    }
  }, [])

  // Feature availability is admin-controlled (see /api/v1/admin/handover).
  // Owners view status read-only and manage their sub-user agents + see the
  // live monitor. If they want the feature disabled, they ask the admin.

  const delAgent = async (id) => {
    if (!window.confirm('Delete this agent? They will be signed out immediately.')) return
    try {
      await api.delete(`/customer/handover/sub-users/${id}`)
      load()
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed.')
    }
  }

  // Live updates: SSE + 5s polling fallback. If the ingress buffers SSE
  // responses, polling keeps the owner's live view up to date.
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    // 1) SSE for instant updates
    const url = `${API_PREFIX}/customer/handover/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    esRef.current = es

    const refresh = () => load()
    es.addEventListener('handover.requested', refresh)
    es.addEventListener('handover.claimed', refresh)
    es.addEventListener('handover.takeover', refresh)
    es.addEventListener('handover.ended', (evt) => {
      refresh()
      try {
        const d = JSON.parse(evt.data)
        if (openIdRef.current && d.handover_id === openIdRef.current) {
          setOpenId(null); setOpenDetail(null)
        }
      } catch {}
    })
    const onMsg = (evt) => {
      try {
        const d = JSON.parse(evt.data)
        if (openIdRef.current && d.handover_id === openIdRef.current) loadDetail(openIdRef.current)
      } catch {}
    }
    es.addEventListener('handover.agent_message', onMsg)
    es.addEventListener('handover.visitor_message', onMsg)

    // 2) Polling fallback — 5s cadence covers SSE being buffered by the proxy
    const pollTimer = setInterval(() => {
      load()
      if (openIdRef.current) loadDetail(openIdRef.current)
    }, 5000)

    load()
    return () => {
      try { es.close() } catch {}
      clearInterval(pollTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { openIdRef.current = openId }, [openId])

  // Server timestamps are UTC. If the string has no tz suffix we append
  // 'Z' before parsing, otherwise the browser would interpret it as LOCAL
  // and show "5h ago" bugs in UTC+5:30 timezones.
  const parseServerTime = (iso) => {
    if (iso instanceof Date) return iso
    if (!iso || typeof iso !== 'string') return null
    const hasTz = /Z$|[+-]\d\d:?\d\d$/.test(iso)
    return new Date(hasTz ? iso : iso + 'Z')
  }

  const fmtTime = (iso) => {
    if (!iso) return '—'
    const d = parseServerTime(iso)
    if (!d || isNaN(d.getTime())) return '—'
    const diff = Date.now() - d.getTime()
    if (diff < 60_000) return 'just now'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
    return d.toLocaleString()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Human Handover</h2>
          <p className="text-sm text-slate-500">Let real agents take over chats when the bot can't help. You can monitor every handover here in real time, but to reply to visitors you must create an agent sub-user and sign in as them.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {enabled ? 'Handover enabled by admin' : 'Handover disabled'}
          </span>
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Agents */}
        <section className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Agents</h3>
              <p className="text-xs text-slate-500">{stats.total_agents} total · {stats.active_agents} active</p>
            </div>
            <button onClick={() => { setEditing(null); setShowCreate(true) }} className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700">+ Add agent</button>
          </div>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[55vh] overflow-y-auto">
            {agents.length === 0 ? (
              <div className="p-6 text-sm text-slate-400 text-center">No agents yet. Add one to answer visitor handovers.</div>
            ) : agents.map((a) => (
              <div key={a.id} className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {a.email}
                    {a.agent_display_id && <span className="ml-2 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{a.agent_display_id}</span>}
                    {!a.is_active && <span className="ml-2 inline-flex items-center rounded bg-rose-50 text-rose-700 px-1.5 py-0.5">inactive</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {a.active_handovers} active · {a.total_handovers} total · last login {fmtTime(a.last_login_at)}
                  </div>
                </div>
                <button onClick={() => { setEditing(a); setShowCreate(true) }} className="text-xs px-2 py-1 border border-slate-300 rounded bg-white hover:bg-slate-50">Edit</button>
                <button onClick={() => delAgent(a.id)} className="text-xs px-2 py-1 border border-rose-300 text-rose-700 rounded bg-white hover:bg-rose-50">Delete</button>
              </div>
            ))}
          </div>
        </section>

        {/* Live monitor */}
        <section className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Live Handovers</h3>
              <p className="text-xs text-slate-500">Read-only — only an assigned agent can message the visitor.</p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
            </span>
          </div>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[55vh] overflow-y-auto">
            {live.length === 0 ? (
              <div className="p-6 text-sm text-slate-400 text-center">No handover activity.</div>
            ) : live.map((h) => (
              <div key={h.handover_id} className="p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => { setOpenId(h.handover_id); loadDetail(h.handover_id) }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{h.first_message || <em className="text-slate-400">No preview</em>}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className={
                      h.status === 'requested' ? 'inline-flex rounded bg-amber-100 text-amber-800 px-1.5 py-0.5' :
                      h.status === 'claimed' || h.status === 'active' ? 'inline-flex rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5' :
                      'inline-flex rounded bg-slate-100 text-slate-700 px-1.5 py-0.5'
                    }>{h.status}</span>
                    <span>·</span>
                    <span>{h.message_count} msgs</span>
                    {h.agent_display_id && <span className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700">{h.agent_display_id}</span>}
                    <span>· started {fmtTime(h.requested_at)}</span>
                    {h.requested_by === 'agent' && <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5">agent-initiated</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Create / edit agent modal */}
      {showCreate && (
        <AgentForm
          agent={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load() }}
        />
      )}

      {/* Read-only chat panel */}
      {openId && openDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">
                  {openDetail.visitor_id || 'Visitor'} ·{' '}
                  <span className={
                    openDetail.status === 'ended' ? 'text-slate-500' :
                    openDetail.status === 'active' || openDetail.status === 'claimed' ? 'text-emerald-600' :
                    'text-amber-600'
                  }>{openDetail.status}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {openDetail.agent_display_id ? `handled by ${openDetail.agent_display_id}` : 'no agent yet'} ·{' '}
                  started {fmtTime(openDetail.requested_at)}
                </div>
              </div>
              <button onClick={() => { setOpenId(null); setOpenDetail(null) }} className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
              {/* Hide handover_wait / handover_waiting system notes from
                  the owner view too — they're visitor-facing bubbles that
                  just clutter the chat history for owners monitoring it. */}
              {(openDetail.messages || [])
                .filter((m) => m.agent_used !== 'handover_wait' && m.agent_used !== 'handover_waiting')
                .map((m) => {
                  const isUser = m.role === 'user'
                  const isHuman = m.agent_used === 'human_agent'
                  const isSys = m.agent_used === 'handover_request' || m.agent_used === 'handover_unavailable'
                  return (
                    <div key={m.id} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        isUser ? 'bg-white border border-slate-200' :
                        isHuman ? 'bg-indigo-600 text-white' :
                        isSys ? 'bg-amber-100 text-amber-900 border border-amber-200' :
                        'bg-slate-200'
                      }`}>
                        {m.content}
                        <div className={`mt-1 text-[10px] opacity-70 ${isUser ? 'text-slate-500' : ''}`}>
                          {isHuman ? (m.sender_display_id || 'agent') : (isUser ? 'visitor' : (m.agent_used || 'bot'))}
                          {' · '}{fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
            <div className="p-3 border-t bg-slate-100 text-xs text-slate-600">
              Owner view is read-only. To send a message, sign in as a handover agent sub-user.
            </div>
          </div>
        </div>
      )}

      {/* Telegram agent alerts */}
      <TelegramAgentAlerts agents={agents} fmtTime={fmtTime} />
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────────────
// Telegram Agent Alerts section
//
// Lets the Customer Admin link their handover agents to Cognavia's
// shared Telegram bot (@CognaAssistAgentBot). On a handover request the
// backend sends a Telegram message with a Claim button to every linked
// agent — so they can see and claim handovers without being on the
// dashboard.
// ─────────────────────────────────────────────────────────────────────────
function TelegramAgentAlerts({ agents, fmtTime }) {
  const [linkedAgents, setLinkedAgents] = useState([])
  const [pendingCodes, setPendingCodes] = useState([])
  const [generating, setGenerating] = useState(null)   // agent_user_id currently generating
  const [newCode, setNewCode] = useState(null)         // the fresh code modal
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try {
      const [linkedRes, pendingRes] = await Promise.all([
        api.get('/customer/handover/telegram/agents'),
        api.get('/customer/handover/telegram/link-codes'),
      ])
      setLinkedAgents(linkedRes.data || [])
      setPendingCodes(pendingRes.data || [])
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load Telegram agents.')
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 10000) // poll every 10s so link-redemption updates visibly
    return () => clearInterval(t)
  }, [load])

  const generate = async (agent_user_id) => {
    setGenerating(agent_user_id)
    try {
      const r = await api.post('/customer/handover/telegram/link-codes', { agent_user_id })
      setNewCode(r.data)
      await load()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not generate link code.')
    } finally {
      setGenerating(null)
    }
  }

  const revoke = async (link_id) => {
    if (!window.confirm('Revoke this agent\'s Telegram link? They will stop receiving alerts.')) return
    try {
      await api.delete(`/customer/handover/telegram/agents/${link_id}`)
      await load()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Revoke failed.')
    }
  }

  // Build a map of agent_user_id → linked / pending status so we can
  // show per-agent action buttons cleanly below.
  const linkedByAgent = new Map(linkedAgents.map(l => [l.agent_user_id, l]))
  const pendingByAgent = new Map(pendingCodes.map(p => [p.agent_user_id, p]))

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <span>Telegram agent alerts</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Beta</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Agents receive handover requests on their phone and can claim with one tap — no dashboard needed.
            They just need Telegram installed and a one-time link code from you.
          </p>
        </div>
      </div>

      {err && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{err}</div>}

      {agents.length === 0 ? (
        <div className="p-6 text-sm text-slate-400 text-center border border-dashed border-slate-200 rounded-md">
          Add handover agents first (above), then link them to Telegram here.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
          {agents.map((a) => {
            const linked = linkedByAgent.get(a.id)
            const pending = pendingByAgent.get(a.id)
            return (
              <div key={a.id} className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {a.full_name || a.email}{' '}
                    <span className="text-xs text-slate-500">({a.agent_display_id})</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {linked ? (
                      <>
                        ✓ Linked as{' '}
                        <span className="font-mono">
                          {linked.telegram_username ? `@${linked.telegram_username}` : (linked.telegram_first_name || 'Telegram user')}
                        </span>{' · '}
                        {fmtTime(linked.linked_at)}
                      </>
                    ) : pending ? (
                      <>⏳ Link sent · code <span className="font-mono">{pending.code}</span> expires {fmtTime(pending.expires_at)}</>
                    ) : (
                      <>Not linked yet</>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {linked ? (
                    <button
                      onClick={() => revoke(linked.id)}
                      className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    >
                      Revoke
                    </button>
                  ) : pending ? (
                    <>
                      <button
                        onClick={() => { navigator.clipboard?.writeText(pending.deep_link); alert('Link copied — share with ' + (a.full_name || a.email)) }}
                        className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800"
                      >
                        Copy link
                      </button>
                      <button
                        onClick={() => generate(a.id)}
                        disabled={generating === a.id}
                        className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
                      >
                        {generating === a.id ? '…' : 'Regenerate'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => generate(a.id)}
                      disabled={generating === a.id}
                      className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {generating === a.id ? 'Generating…' : 'Generate link'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Fresh code modal */}
      {newCode && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
            <h4 className="text-base font-semibold text-slate-900">Share this link</h4>
            <p className="text-sm text-slate-600 mt-1">
              Send this to your agent any way you like (email, Slack, WhatsApp).
              When they tap it, Telegram will open and automatically link them.
              <br />
              <span className="text-xs text-slate-500">Expires in 1 hour. Single use.</span>
            </p>
            <div className="mt-4 p-3 rounded-md border border-slate-200 bg-slate-50 font-mono text-xs break-all">
              {newCode.deep_link}
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => { navigator.clipboard?.writeText(newCode.deep_link) }}
                className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                📋 Copy link
              </button>
              <button
                onClick={() => setNewCode(null)}
                className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

function AgentForm({ agent, onClose, onSaved }) {
  const isEdit = !!agent
  const [full_name, setFullName] = useState(agent?.full_name || '')
  const [email, setEmail] = useState(agent?.email || '')
  const [password, setPassword] = useState('')
  const [agent_display_id, setDisplayId] = useState(agent?.agent_display_id || '')
  const [is_active, setIsActive] = useState(agent?.is_active ?? true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const save = async () => {
    setSaving(true); setErr('')
    try {
      if (isEdit) {
        const body = { full_name, agent_display_id, is_active }
        if (password) body.password = password
        await api.put(`/customer/handover/sub-users/${agent.id}`, body)
      } else {
        await api.post('/customer/handover/sub-users', {
          full_name, email, password, agent_display_id,
        })
      }
      onSaved()
    } catch (e) {
      const d = e?.response?.data?.detail
      setErr(Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join('; ') : (d || 'Save failed.'))
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-lg font-semibold">{isEdit ? 'Edit agent' : 'Add handover agent'}</h3>
        {err && <div className="rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700">{err}</div>}
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Full name</label>
          <input value={full_name} onChange={(e) => setFullName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
        </div>
        {!isEdit && (
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
            <p className="text-[10px] text-slate-500 mt-1">Must be unique across the entire CognaAssist platform.</p>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            {isEdit ? 'New password (leave blank to keep)' : 'Password'}
          </label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded" placeholder={isEdit ? '••••••••' : 'min 8 chars'} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Agent ID (shown to teammates)</label>
          <input value={agent_display_id} onChange={(e) => setDisplayId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded" placeholder="support-01" />
          <p className="text-[10px] text-slate-500 mt-1">Unique within your account.</p>
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={is_active} onChange={(e) => setIsActive(e.target.checked)} /> Active
          </label>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-slate-300 bg-white">Cancel</button>
          <button disabled={saving} onClick={save} className="text-sm px-4 py-1.5 rounded bg-indigo-600 text-white disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create agent')}
          </button>
        </div>
      </div>
    </div>
  )
}
